import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import {
  resolveFilterChipLabel,
  type FilterChipEditContext,
  type FilterItem,
  type FilterItemLabel,
  type ObjectSearchRequest,
} from '@bizzdesign/data-access-search';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import type { AttributeCategoryDefinitionDto } from '@bizzdesign/shared/models';
import { TranslateDisplayTextPipe } from '@bizzdesign/shared/pipes';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { InteractiveFilterValueComponent } from '../interactive-filter-value/interactive-filter-value.component';

/**
 * Colour an element-type icon by dropping the `bd-monotone-icon` modifier that the
 * shared label builders apply for the greyscale applied-filter chips.
 *
 * @param icon - The space-separated icon class list.
 * @returns The class list without the monotone modifier, so the coloured variant renders.
 */
function toColoredIcon(icon: string): string {
  return icon
    .split(' ')
    .filter((className) => className !== '' && className !== 'bd-monotone-icon')
    .join(' ');
}

function toColoredSegment(segment: FilterItemLabel): FilterItemLabel {
  return segment.icon
    ? { ...segment, icon: toColoredIcon(segment.icon) }
    : segment;
}

/**
 * A single applied-filter chip: it renders the filter as natural-language text and
 * delegates inline editing of the **value** to {@link InteractiveFilterValueComponent}.
 *
 * @remarks Presentational shell only — it never mutates a store. Value edits bubble
 * up through {@link updateFilter} for a container to apply; the condition/operator and
 * the relation kind (role, is/isNot, target kind) are never edited here.
 */
@Component({
  selector: 'bd-interactive-filter-chip',
  imports: [
    AsyncPipe,
    ButtonModule,
    InteractiveFilterValueComponent,
    SharedI18nModule,
    TooltipModule,
    TranslateDisplayTextPipe,
  ],
  templateUrl: './interactive-filter-chip.component.html',
  styleUrl: './interactive-filter-chip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InteractiveFilterChipComponent {
  filter = input.required<FilterItem>();
  attributeCategoryDefinitions = input<AttributeCategoryDefinitionDto[]>([]);
  editContext = input<FilterChipEditContext>();
  removable = input<boolean>(true);
  disabled = input<boolean>(false);

  updateFilter = output<FilterItem>();
  remove = output<FilterItem>();
  searchObjects = output<ObjectSearchRequest>();
  searchUsers = output<string>();

  private readonly chipLabel = computed(() =>
    resolveFilterChipLabel(this.filter()),
  );

  /** Element-type icon shown at the start of the chip. */
  readonly icon = computed<string | undefined>(() => {
    const icon = this.chipLabel().icon;
    return icon ? toColoredIcon(icon) : undefined;
  });

  /** Attribute category, shown on hover instead of as chip text. */
  readonly tooltip = computed<string>(() => this.chipLabel().tooltip ?? '');

  /** What the filter is about, rendered in the label type style. */
  readonly subject = computed<FilterItemLabel | undefined>(
    () => this.chipLabel().subject,
  );

  /** Connecting words between the subject and the value. */
  readonly connectors = computed<FilterItemLabel[]>(
    () => this.chipLabel().connectors,
  );

  /** The trailing value segment handed to the value editor; `undefined` when the filter has no editable value. */
  readonly valueSegment = computed<FilterItemLabel | undefined>(() => {
    const value = this.chipLabel().value;
    return value ? toColoredSegment(value) : undefined;
  });

  /** Whether the filter exposes an editable value; controls whether the value editor is shown. */
  readonly hasEditableValue = computed(() => this.valueSegment() !== undefined);

  onRemove(): void {
    this.remove.emit(this.filter());
  }
}
